import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {CombosService} from '../../../../../../shared/services/combos.service';
import {ErrorService} from '../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {EstablecimientosService} from '../establecimientos.service';
import {forkJoin} from 'rxjs';
import {Ciudad, Departamento, Pais, Zona} from '../../../../../../shared/services/interfaces/combos.interface';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {TextareaFieldComponent} from '@shared/components/form/textarea/textarea-field.component';
import {FormStatusService} from '../../../../../../shared/services/form-status.service';
import {Card} from '../../../../../../shared/components/common/card/card';
import {LabelComponent} from '../../../../../../shared/components/form/label/label.component';
import {EditionButtons} from '../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import {Spinner} from '../../../../../../shared/components/ui/spinner/spinner';
import {PageHeaderComponent} from '../../../../../../shared/components/common/page-header/page-header.component';
import {MatTab, MatTabContent, MatTabGroup, MatTabLabel} from '@angular/material/tabs';
// Pestañas ocultas a pedido del cliente (ver template)
// import {EstablecimientosContactos} from '../../establecimientos-contactos/establecimientos-contactos';
// import {EstablecimientosServicios} from '../../establecimientos-servicios/establecimientos-servicios';
// import {Presupuestos} from '../../presupuestos/presupuestos';
import {Animales} from '../../../animales/animales';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { LocationCoordinates } from '@shared/components/form/location-picker/location-picker.component';
import { LocationPickerTriggerComponent } from '@shared/components/form/location-picker/location-picker-trigger.component';
import { coordinateValidator } from '@shared/validators/coordinate.validator';

@Component({
  selector: 'app-detalle-establecimiento',
  imports: [
    HasPermissionDirective,
    Card,
    LabelComponent,
    ReactiveFormsModule,
    EditionButtons,
    Spinner,
    PageHeaderComponent,
    MatTabGroup,
    MatTab,
    MatTabContent,
    MatTabLabel,
    // EstablecimientosContactos,
    // EstablecimientosServicios,
    // Presupuestos,
    Animales,
    SelectFieldComponent,
    TextareaFieldComponent,
    InputFieldComponent,
    AppFormComponent,
    LocationPickerTriggerComponent,
  ],
  templateUrl: './detalle-establecimiento.html',
  styles: ``
})
export class DetalleEstablecimiento implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  editForm!: FormGroup;
  loading = false;
  process = false;
  idCliente: number | null = null;
  idEstablecimiento!: number;
  paises: Pais[] = [];
  departamentos: Departamento[] = [];
  ciudades: Ciudad[] = [];
  zonas: Zona[] = [];

  objetivoOptions: SelectOption[] = [
    { value: 'E', label: 'Producción de embriones' },
    { value: 'P', label: 'Preñez' },
    { value: 'X', label: 'Producción de Embriones/Preñez' },
  ];
  get zonasOptions(): SelectOption[] { return this.zonas.map(x => ({ value: x.id, label: x.nombre })); }
  editable = true;
  selectedIndex = 0;
  // Solo lo usa la pestaña de Presupuestos, hoy oculta
  idServicioSolicitado: number | null = null;
  paisNombre: string = '';
  departamentoNombre: string = '';
  ciudadNombre: string = '';
  selectedCiudadId: number | null = null;

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private establecimientosService = inject(EstablecimientosService);

  ngOnInit(): void {
    this.idCliente = Number(this.route.snapshot.paramMap.get('id'));
    this.idEstablecimiento = Number(this.route.snapshot.paramMap.get('idEstablecimiento'));

    // Leer el queryParam 'tab' para establecer la pestaña activa
    const tabParam = this.route.snapshot.queryParams['tab'];
    if (tabParam !== undefined) {
      // Solo quedan dos pestañas visibles: Cabecera (0) y Animales (1)
      const tab = Number(tabParam);
      this.selectedIndex = Number.isFinite(tab) ? Math.min(Math.max(tab, 0), 1) : 0;
    }

    this.getCombos();
    this.makeForm();
    this.getAllZonas();
    this.getEstablecimiento();
  }

  private getCombos(): void {
    const paises = this.combosService.getPaisesCombo();

    forkJoin([paises]).subscribe({
      next: (response) => {
        this.paises = response[0].data;
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private makeForm(): void {
    this.editForm = this.fB.group({
      nombre: [{ value: '', disabled: this.editable }, Validators.required],
      objetivo: [{ value: '', disabled: this.editable }, Validators.required],
      ubi_zona_id: [{ value: '', disabled: this.editable }, Validators.required],
      latitud: [{ value: '', disabled: this.editable }, coordinateValidator(-90, 90)],
      longitud: [{ value: '', disabled: this.editable }, coordinateValidator(-180, 180)],
      km: [{ value: '', disabled: this.editable }, Validators.min(0)],
      direccion: [{ value: '', disabled: this.editable }]
    });

    this.editForm.get('ubi_zona_id')?.valueChanges.subscribe(value => {
      if (value) {
        this.autoFillLocationFields(value);
      }
    });
  }

  onCoordinatesChange(coordinates: LocationCoordinates): void {
    this.editForm.patchValue(coordinates);
    this.editForm.get('latitud')?.markAsDirty();
    this.editForm.get('longitud')?.markAsDirty();
  }

  private getDepartamentos(ubi_pais_id: number): void {
    this.combosService.getDepartamentosCombo(ubi_pais_id).subscribe({
      next: (response) => {
        this.departamentos = response.data;
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private getCiudades(ubi_departamento_id: number): void {
    this.combosService.getCiudadesCombo(ubi_departamento_id).subscribe({
      next: (response) => {
        this.ciudades = response.data;
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private getAllZonas(): void {
    this.combosService.getAllZonasCombo().subscribe({
      next: (response) => {
        this.zonas = response.data;
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  parseZonaDireccionCompleta(direccion_completa: string): { zona: string, ciudad: string, departamento: string, pais: string } {
    if (!direccion_completa) {
      return { zona: '-', ciudad: '-', departamento: '-', pais: '-' };
    }
    const parts = direccion_completa.split(' / ');
    return {
      zona: parts[0] || '-',
      ciudad: parts[1] || '-',
      departamento: parts[2] || '-',
      pais: parts[3] || '-'
    };
  }

  getSelectedZonaLocation(): { ciudad: string, departamento: string, pais: string } | null {
    const zonaId = this.editForm.get('ubi_zona_id')?.value;
    const selectedZona = this.zonas.find(z => z.id === zonaId);

    if (!selectedZona || !selectedZona.direccion_completa) {
      return null;
    }

    const parsed = this.parseZonaDireccionCompleta(selectedZona.direccion_completa);
    return {
      ciudad: parsed.ciudad,
      departamento: parsed.departamento,
      pais: parsed.pais
    };
  }

  private autoFillLocationFields(zonaId: number): void {
    // Convertir zonaId a número si viene como string desde el select HTML
    const zonaIdNum = Number(zonaId);
    const selectedZona = this.zonas.find(z => z.id === zonaIdNum);

    if (selectedZona && selectedZona.direccion_completa) {
      const location = this.parseZonaDireccionCompleta(selectedZona.direccion_completa);

      // Buscar país por nombre para obtener su ID
      const pais = this.paises.find(p => p.nombre === location.pais);

      if (pais) {
        // Cargar departamentos de ese país
        this.combosService.getDepartamentosCombo(pais.id).subscribe({
          next: (depResponse) => {
            this.departamentos = depResponse.data;
            const departamento = this.departamentos.find(d => d.nombre === location.departamento);

            if (departamento) {
              // Cargar ciudades de ese departamento
              this.combosService.getCiudadesCombo(departamento.id).subscribe({
                next: (ciudadResponse) => {
                  this.ciudades = ciudadResponse.data;
                  const ciudad = this.ciudades.find(c => c.nombre === location.ciudad);

                  // Actualizar las propiedades para mostrar en los inputs
                  this.paisNombre = location.pais;
                  this.departamentoNombre = location.departamento;
                  this.ciudadNombre = location.ciudad;
                  this.selectedCiudadId = ciudad?.id || selectedZona.ubi_ciudad_id;
                }, error: (error) => {
                  this.errorService.handleError(error);
                }
              });
            }
          }, error: (error) => {
            this.errorService.handleError(error);
          }
        });
      }
    }
  }

  getEstablecimiento(): void {
    this.loading = true;
    this.establecimientosService.show(this.idEstablecimiento).subscribe({
      next: (response) => {
        if (response.status) {
          const zona = response.data.direccion?.zona as (Zona & { ciudad?: Ciudad }) | undefined;
          const ciudad = response.data.direccion?.ciudad || zona?.ciudad;
          const departamento = ciudad?.departamento;
          const ciudadId = response.data.direccion?.ubi_ciudad_id
            ?? zona?.ubi_ciudad_id
            ?? ciudad?.id;

          this.editForm.patchValue({
            nombre: response.data.nombre,
            objetivo: response.data.objetivo,
            ubi_zona_id: zona?.id || '',
            latitud: response.data.direccion?.latitud,
            longitud: response.data.direccion?.longitud,
            km: response.data.km || '',
            direccion: response.data.direccion?.direccion
          });

          // Establecer los nombres de ubicación para mostrar
          this.selectedCiudadId = ciudadId;
          if (zona?.direccion_completa) {
            const location = this.parseZonaDireccionCompleta(zona.direccion_completa);
            this.paisNombre = location.pais;
            this.departamentoNombre = location.departamento;
            this.ciudadNombre = location.ciudad;
          }
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  updateEstablecimiento(): void {
    this.process = true;
    const jsonData = {
      ...this.editForm.value,
      ubi_ciudad_id: this.selectedCiudadId
    };

    this.establecimientosService.update(this.idEstablecimiento, jsonData).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Establecimiento actualizado correctamente');
          this.refreshEstablecimiento();
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.process = false;
    });
  }

  refreshEstablecimiento(): void {
    this.getEstablecimiento();
    this.editableSwitcher();
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  goBack(): void {
    this.router.navigate(['../../'], {relativeTo: this.route, queryParams: {tab: 1}});
  }

  // Sin la pestaña de Servicios ya nadie dispara la navegación a Presupuestos.
  // Se conserva comentado junto con las pestañas ocultas.
  // goToPresupuestos(idServicioSolicitado: number): void {
  //   this.selectedIndex = 3;
  //   this.idServicioSolicitado = idServicioSolicitado;
  // }
}
